import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Chip,
    Divider,
    FormControlLabel,
    Grid,
    LinearProgress,
    MenuItem,
    Paper,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBrain,
    faChartColumn,
    faCircleCheck,
    faGaugeHigh,
    faMicrophone,
    faPlugCircleCheck,
    faTriangleExclamation,
    faWandMagicSparkles,
} from '@fortawesome/pro-solid-svg-icons';
import { useSnackbar } from 'notistack';
import { useApplicationContext } from 'ApplicationContext';
import SettingsPageHeader from './SettingsPageHeader';
import { settingsApi } from './settingsApi';
import { AiCostReport } from './AiCostReport';

type Provider = 'Anthropic' | 'Ollama';
type SpeechProvider = 'LocalWhisper' | 'OpenAiCompatible';

/** Zustand des Formulars — getrennt vom Server-DTO, weil Geheimnisse anders behandelt werden. */
interface AiSettingsForm {
    enabled: boolean;
    provider: Provider;
    baseUrl: string;
    model: string;
    fastModel: string;
    cheapModel: string;
    effort: string;
    maxIterations: number;
    maxOutputTokens: number;
    timeoutSeconds: number;
    monthlyBudgetEur: number;
    usdToEur: number;
    warnAtPercent: number;
    latencyBudgetMs: number;
    speechEnabled: boolean;
    speechProvider: SpeechProvider;
    speechBaseUrl: string;
    speechModel: string;
}

interface AiSettingsDto extends AiSettingsForm {
    hasApiKey: boolean;
    hasSpeechApiKey: boolean;
    /** Ein Schlüssel liegt in der Ablage, ließ sich aber nicht entschlüsseln. */
    apiKeyUnreadable: boolean;
    speechApiKeyUnreadable: boolean;
}

interface AiTestResult {
    success: boolean;
    message: string;
    elapsedMilliseconds: number;
    withinLatencyBudget: boolean | null;
    model: string | null;
    reply: string | null;
}

interface AiUsage {
    month: string;
    drafts: number;
    inputTokens: number;
    outputTokens: number;
    cachedTokens: number;
    estimatedEur: number;
    budgetEur: number;
    overBudget: boolean;
    nearBudget: boolean;
}

interface QuestionBacklog {
    missingCount: number;
    pendingCount: number;
}

const EFFORTS = ['low', 'medium', 'high', 'xhigh', 'max'];

/** Vorschläge je Anbieter. Modelle kommen und gehen, deshalb bleibt der gespeicherte Wert immer wählbar. */
const MODEL_SUGGESTIONS: Record<Provider, string[]> = {
    Anthropic: ['claude-opus-5', 'claude-sonnet-5', 'claude-haiku-4-5-20251001'],
    Ollama: ['qwen3:32b', 'llama3.3:70b', 'mistral-small'],
};

/**
 * Auswahlliste eines Modellfelds: Vorschläge plus der gespeicherte Wert. Letzteres ist wichtig —
 * sonst verschwindet ein von Hand gepflegtes Modell aus der Liste und wird still überschrieben.
 */
const modelOptions = (provider: Provider, current: string): string[] => {
    const out: string[] = [];
    for (const m of [...(MODEL_SUGGESTIONS[provider] ?? []), current]) {
        if (m && out.indexOf(m) < 0) out.push(m);
    }
    return out;
};

const emptyForm: AiSettingsForm = {
    enabled: false,
    provider: 'Anthropic',
    baseUrl: '',
    model: 'claude-opus-5',
    fastModel: '',
    cheapModel: 'claude-haiku-4-5-20251001',
    effort: 'medium',
    maxIterations: 25,
    maxOutputTokens: 8000,
    timeoutSeconds: 120,
    monthlyBudgetEur: 0,
    usdToEur: 0,
    warnAtPercent: 80,
    latencyBudgetMs: 4000,
    speechEnabled: false,
    speechProvider: 'LocalWhisper',
    speechBaseUrl: '',
    speechModel: 'large-v3-turbo',
};

/** Ein Abschnitt der Seite — Kopf mit Symbol, kurze Begründung, dann die Felder. */
const Section: React.FC<{
    icon: typeof faBrain;
    title: string;
    hint?: string;
    chip?: React.ReactNode;
    children: React.ReactNode;
}> = ({ icon, title, hint, chip, children }) => (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2.5 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: hint ? 0.5 : 2, flexWrap: 'wrap' }}>
            <Box sx={{ color: 'primary.main' }}><FontAwesomeIcon icon={icon} /></Box>
            <Typography variant="h6">{title}</Typography>
            {chip}
        </Stack>
        {hint && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {hint}
            </Typography>
        )}
        {children}
    </Paper>
);

/**
 * Einstellungen des KI-Assistenten.
 *
 * Deutsch fest verdrahtet — wie die Geschwisterseiten (AD-Anbindung, Standort-Dienst). i18n nur
 * hier einzuführen wäre die größere Inkonsistenz.
 */
export const AiSettings: React.FC = () => {
    const context = useApplicationContext();
    const { enqueueSnackbar } = useSnackbar();
    // Über request() statt api.instance — sonst fehlt der Authorization-Header.
    const http = useMemo(() => settingsApi(context.api), [context.api]);

    const [form, setForm] = useState<AiSettingsForm>(emptyForm);
    const [saved, setSaved] = useState<AiSettingsForm>(emptyForm);
    const [hasApiKey, setHasApiKey] = useState(false);
    const [hasAdminApiKey, setHasAdminApiKey] = useState(false);
    const [adminApiKey, setAdminApiKey] = useState<string | null>(null);
    const [hasSpeechApiKey, setHasSpeechApiKey] = useState(false);
    const [keyUnreadable, setKeyUnreadable] = useState(false);
    // null = unverändert lassen; '' = löschen; sonst neu setzen. Spiegelt die Serverregel.
    const [apiKey, setApiKey] = useState<string | null>(null);
    const [speechApiKey, setSpeechApiKey] = useState<string | null>(null);

    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [test, setTest] = useState<AiTestResult | null>(null);
    const [backlog, setBacklog] = useState<QuestionBacklog | null>(null);
    const [usage, setUsage] = useState<AiUsage | null>(null);

    const set = <K extends keyof AiSettingsForm>(key: K, value: AiSettingsForm[K]) =>
        setForm((f) => ({ ...f, [key]: value }));

    const loadBacklog = useCallback(async () => {
        try {
            const r = await http.get<QuestionBacklog>('/AiSettings/QuestionBacklog');
            setBacklog(r.data);
        } catch {
            // Der Katalog kann fehlen (Erststart) — dann bleibt der Block einfach leer.
            setBacklog(null);
        }
    }, [http]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            /*
             * Lokal erweitert statt den erzeugten Client anzufassen: `src/apiClient/Api.ts` entsteht
             * aus dem Swagger eines LAUFENDEN Servers (`npm run generate-api`) und ist hier älter
             * als die Schnittstelle. Von Hand nachzupflegen hieße, die Änderung beim nächsten
             * Erzeugen wieder zu verlieren.
             */
            const r = await http.get<AiSettingsDto & {
                cheapModel?: string | null;
                hasAdminApiKey?: boolean;
                usdToEur?: number;
            }>('/AiSettings/Settings');
            const { hasApiKey: hk, hasAdminApiKey: hak, hasSpeechApiKey: hsk,
                apiKeyUnreadable: broken, speechApiKeyUnreadable: _sBroken, ...rest } = r.data;
            setKeyUnreadable(!!broken);
            const next = { ...emptyForm, ...rest };
            setForm(next);
            setSaved(next);
            setHasApiKey(hk);
            setHasAdminApiKey(!!hak);
            setHasSpeechApiKey(hsk);
            setApiKey(null);
            setAdminApiKey(null);
            setSpeechApiKey(null);
            await loadBacklog();
            try {
                const u = await http.get<AiUsage>('/AiSettings/Usage');
                setUsage(u.data);
            } catch {
                setUsage(null);
            }
        } catch (e) {
            context.apiError(e);
        } finally {
            setLoading(false);
        }
    }, [context, http, loadBacklog]);

    useEffect(() => {
        void load();
    }, [load]);

    const isAnthropic = form.provider === 'Anthropic';

    /** Ungespeicherte Änderungen — auch ein neu getipptes Geheimnis zählt. */
    const dirty = useMemo(
        () => JSON.stringify(form) !== JSON.stringify(saved) || apiKey !== null || speechApiKey !== null,
        [form, saved, apiKey, speechApiKey],
    );

    /**
     * Was fehlt, damit die Einstellungen tragen. Wird vor dem Speichern angezeigt statt danach —
     * ein Serverfehler auf eine Eingabe, die man schon beim Tippen sieht, ist ärgerlich.
     */
    const problems = useMemo(() => {
        const out: string[] = [];
        if (!form.model.trim()) out.push('Modell darf nicht leer sein.');
        if (isAnthropic && !hasApiKey && !apiKey) out.push('Anthropic braucht einen API-Schlüssel.');
        if (!isAnthropic && !form.baseUrl.trim()) out.push('Ollama braucht eine Basis-URL.');
        if (form.speechEnabled && !form.speechBaseUrl.trim())
            out.push('Für die Spracherkennung fehlt die Basis-URL.');
        return out;
    }, [form, isAnthropic, hasApiKey, apiKey]);

    /** Nichts eingerichtet und nichts angefasst → Einstiegshilfe zeigen. */
    const firstRun = !loading && !form.enabled && !hasApiKey && !form.baseUrl && !dirty;

    const save = async () => {
        setBusy(true);
        try {
            await http.put('/AiSettings/Settings', { ...form, apiKey, adminApiKey, speechApiKey });
            enqueueSnackbar('Einstellungen gespeichert.', { variant: 'success' });
            setTest(null);
            await load();
        } catch (e) {
            context.apiError(e);
        } finally {
            setBusy(false);
        }
    };

    const runTest = async (fast: boolean) => {
        setBusy(true);
        setTest(null);
        try {
            const r = await http.post<AiTestResult>('/AiSettings/TestConnection', { fast });
            setTest(r.data);
        } catch (e) {
            context.apiError(e);
        } finally {
            setBusy(false);
        }
    };

    const generateQuestions = async () => {
        setBusy(true);
        try {
            const r = await http.post<QuestionBacklog>('/AiSettings/GenerateMissingQuestions');
            setBacklog(r.data);
            enqueueSnackbar(`${r.data.pendingCount} Anforderungen eingestellt — läuft im Hintergrund.`,
                { variant: 'success' });
        } catch (e) {
            context.apiError(e);
        } finally {
            setBusy(false);
        }
    };

    /** Geheimnis-Feld: zeigt, DASS etwas hinterlegt ist, nie den Wert. */
    const secretField = (
        label: string,
        stored: boolean,
        value: string | null,
        onChange: (v: string | null) => void,
        required: boolean,
    ) => (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
            <TextField
                fullWidth size="small" type="password" label={label}
                value={value ?? ''}
                disabled={value === null && stored}
                error={required && !stored && !value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={stored ? '•••••••• (gespeichert)' : required ? 'erforderlich' : ''}
                helperText={value === null && stored
                    ? 'Hinterlegt. Zum Ersetzen auf „Ändern“ klicken — der gespeicherte Wert wird nie ausgeliefert.'
                    : 'Wird verschlüsselt gespeichert und verlässt den Server nicht.'}
            />
            {stored && (
                <Button size="small" sx={{ mt: 0.5 }} onClick={() => onChange(value === null ? '' : null)}>
                    {value === null ? 'Ändern' : 'Abbrechen'}
                </Button>
            )}
        </Stack>
    );

    return (
        <Box sx={{ pb: 2 }}>
            <SettingsPageHeader
                sectionKey="ai"
                chip={!loading && (
                    <Chip
                        size="small"
                        color={form.enabled ? (problems.length ? 'warning' : 'success') : 'default'}
                        variant={form.enabled ? 'filled' : 'outlined'}
                        icon={<FontAwesomeIcon
                            icon={form.enabled && !problems.length ? faCircleCheck : faTriangleExclamation}
                            style={{ fontSize: '0.8rem' }} />}
                        label={!form.enabled ? 'Abgeschaltet' : problems.length ? 'Unvollständig' : 'Aktiv'}
                    />
                )}
            />

            {loading && <LinearProgress sx={{ mb: 2 }} />}

            {firstRun && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    <AlertTitle>Noch nicht eingerichtet</AlertTitle>
                    Anbieter wählen, Zugangsdaten eintragen, speichern — danach mit „Verbindung testen"
                    prüfen. Gesprächsfragen lassen sich erst erzeugen, wenn der Assistent eingeschaltet ist.
                </Alert>
            )}

            <Grid container spacing={2}>
                {/* ---------- Textmodell ---------- */}
                <Grid size={12}>
                    <Section icon={faBrain} title="Textmodell" hint="Das Modell, das die Gesprächsführung übernimmt.">
                        <FormControlLabel
                            control={<Switch checked={form.enabled}
                                onChange={(e) => set('enabled', e.target.checked)} />}
                            label="Assistent eingeschaltet"
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            Aus = der Assistent erscheint nirgends und erzeugt nichts.
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <TextField select fullWidth size="small" label="Anbieter"
                                    value={form.provider}
                                    onChange={(e) => set('provider', e.target.value as Provider)}
                                >
                                    <MenuItem value="Anthropic">Anthropic (Claude, gehostet)</MenuItem>
                                    <MenuItem value="Ollama">Ollama (lokal)</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid size={{ xs: 12, md: 8 }}>
                                <TextField fullWidth size="small" label="Basis-URL"
                                    value={form.baseUrl}
                                    error={!isAnthropic && !form.baseUrl.trim()}
                                    onChange={(e) => set('baseUrl', e.target.value)}
                                    placeholder={isAnthropic ? 'leer = Vorgabe des SDK' : 'http://192.168.1.160:11434'}
                                    helperText={isAnthropic
                                        ? 'Leer lassen, außer ihr nutzt einen abweichenden Endpunkt.'
                                        : 'Pflicht. Dieselbe Instanz, die auch die Embeddings rechnet.'}
                                />
                            </Grid>

                            {isAnthropic && keyUnreadable && (
                                <Grid size={12}>
                                    <Alert severity="error">
                                        <AlertTitle>Gespeicherter Schlüssel nicht lesbar</AlertTitle>
                                        Es liegt ein Schlüssel in der Ablage, er lässt sich aber nicht
                                        entschlüsseln — typisch nach neu erzeugten DataProtection-Keys
                                        oder abweichendem <code>DataProtection:ApplicationName</code>.
                                        Bitte den Schlüssel neu eintragen.
                                    </Alert>
                                </Grid>
                            )}
                            {isAnthropic && (
                                <Grid size={12}>
                                    {secretField('API-Schlüssel', hasApiKey, apiKey, setApiKey, true)}
                                </Grid>
                            )}
                            {isAnthropic && (
                                <Grid size={12}>
                                    {secretField('Admin-Schlüssel (Kostenberichte)', hasAdminApiKey,
                                        adminApiKey, setAdminApiKey, false)}
                                    <Typography variant="caption" color="text.secondary"
                                        sx={{ display: 'block', mt: 0.5 }}>
                                        Optional, aber der einzige Weg zu den <b>tatsächlichen</b> Kosten
                                        statt der eigenen Schätzung. Ein <b>anderer</b> Schlüssel als oben:
                                        er beginnt mit <code>sk-ant-admin01-</code> und wird in der Konsole
                                        von einem Organisations-Administrator erzeugt. Ein auf einen
                                        Arbeitsbereich beschränkter Schlüssel funktioniert nicht.
                                    </Typography>
                                </Grid>
                            )}

                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField select fullWidth size="small" label="Modell für die großen Schritte"
                                    value={form.model} error={!form.model.trim()}
                                    onChange={(e) => set('model', e.target.value)}
                                    helperText="Nachrechnen, Zusammenfassen, Angebotstexte."
                                >
                                    {modelOptions(form.provider, form.model)
                                        .map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                                </TextField>
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField select fullWidth size="small" label="Schnelles Modell"
                                    value={form.fastModel}
                                    onChange={(e) => set('fastModel', e.target.value)}
                                    helperText={'Für „nächste Frage“. Im Telefonat zählt die Antwortzeit. Leer = dasselbe wie oben.'}
                                >
                                    <MenuItem value="">(wie oben)</MenuItem>
                                    {modelOptions(form.provider, form.fastModel)
                                        .map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                                </TextField>
                            </Grid>
                            {/*
                              * Die dritte Stufe. Drei Modelle, weil die Aufgaben drei verschiedene
                              * sind: Dokumente einordnen ist Mengenarbeit, das Gespräch ist
                              * latenzkritisch, eine Ausschreibung auszuwerten ist Denkarbeit. Ein
                              * Modell für alles heißt, zwei davon falsch zu bedienen.
                              */}
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField select fullWidth size="small" label="Günstiges Modell"
                                    value={form.cheapModel}
                                    onChange={(e) => set('cheapModel', e.target.value)}
                                    helperText="Sichtet Kundenunterlagen: was ist das, trägt es das Angebot? Ein weitergeleiteter Ordner bringt zehn Gerätehandbücher mit. Leer = das schnelle Modell."
                                >
                                    <MenuItem value="">(wie das schnelle)</MenuItem>
                                    {modelOptions(form.provider, form.cheapModel)
                                        .map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                                </TextField>
                            </Grid>

                            <Grid size={{ xs: 6, md: 3 }}>
                                <TextField select fullWidth size="small" label="Aufwandsstufe"
                                    value={form.effort} onChange={(e) => set('effort', e.target.value)}
                                    helperText="Nur großes Modell."
                                >
                                    {EFFORTS.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                                </TextField>
                            </Grid>
                            <Grid size={{ xs: 6, md: 3 }}>
                                <Tooltip title="Notbremse je Gesprächszug — verhindert eine entlaufene Werkzeugschleife.">
                                    <TextField fullWidth size="small" type="number" label="Max. Werkzeugrunden"
                                        value={form.maxIterations}
                                        onChange={(e) => set('maxIterations', Number(e.target.value))} />
                                </Tooltip>
                            </Grid>
                            <Grid size={{ xs: 6, md: 3 }}>
                                <TextField fullWidth size="small" type="number" label="Max. Ausgabe-Token"
                                    value={form.maxOutputTokens}
                                    onChange={(e) => set('maxOutputTokens', Number(e.target.value))} />
                            </Grid>
                            <Grid size={{ xs: 6, md: 3 }}>
                                <TextField fullWidth size="small" type="number" label="Zeitlimit (s)"
                                    value={form.timeoutSeconds}
                                    onChange={(e) => set('timeoutSeconds', Number(e.target.value))} />
                            </Grid>
                        </Grid>
                    </Section>
                </Grid>

                {/* ---------- Antwortzeit und Kosten ---------- */}
                <Grid size={12}>
                    <Section
                        icon={faGaugeHigh}
                        title="Antwortzeit und Kosten"
                        hint="Ein Vorschlag, der im laufenden Telefonat nach 45 Sekunden kommt, ist technisch in Ordnung und fachlich wertlos. Deshalb wird die Antwortzeit gegen eine Erwartung gemessen."
                    >
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <TextField fullWidth size="small" type="number" label="Erwartete Antwortzeit (ms)"
                                    value={form.latencyBudgetMs}
                                    onChange={(e) => set('latencyBudgetMs', Number(e.target.value))}
                                    helperText={'Darüber meldet der Test „zu langsam fürs Telefonat“.'} />
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <TextField fullWidth size="small" type="number" label="Monatsbudget (€)"
                                    value={form.monthlyBudgetEur}
                                    onChange={(e) => set('monthlyBudgetEur', Number(e.target.value))}
                                    helperText="0 = keine Begrenzung." />
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <TextField fullWidth size="small" type="number" label="Warnschwelle (%)"
                                    value={form.warnAtPercent}
                                    onChange={(e) => set('warnAtPercent', Number(e.target.value))} />
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <TextField fullWidth size="small" type="number" label="Kurs USD → EUR"
                                    value={form.usdToEur}
                                    slotProps={{ htmlInput: { step: 0.01, min: 0 } }}
                                    onChange={(e) => set('usdToEur', Number(e.target.value))}
                                    helperText="0 = Tageskurs der EZB. Ein Wert hier übersteuert ihn — etwa ein vorgegebener Planungskurs." />
                            </Grid>
                        </Grid>
                        {usage?.overBudget && (
                            <Alert severity="error" variant="outlined" sx={{ mt: 2 }}>
                                <AlertTitle>Monatsbudget erschöpft</AlertTitle>
                                Weitere Züge werden abgelehnt, bis das Budget angehoben wird oder der
                                Monat wechselt.
                            </Alert>
                        )}
                    </Section>
                </Grid>

                {/* ---------- Kosten ---------- */}
                <Grid size={12}>
                    <Section
                        icon={faChartColumn}
                        title="Kosten"
                        hint="Wo das Geld hingeht — je Monat, je Modell und vor allem je Zweck. Die Summe allein sagt nicht, woran man etwas ändern kann."
                    >
                        <AiCostReport />
                    </Section>
                </Grid>

                {/* ---------- Gesprächsfragen ---------- */}
                <Grid size={12}>
                    <Section
                        icon={faWandMagicSparkles}
                        title="Gesprächsfragen im Katalog"
                        hint="Jede Anforderung braucht eine Frage in Kundensprache und Stichworte, über die ein Kunde sie benennen könnte. Beim Anlegen entsteht beides automatisch — dieser Knopf holt den Bestand nach."
                    >
                        {backlog && (
                            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
                                <Chip size="small"
                                    color={backlog.missingCount === 0 ? 'success' : 'warning'}
                                    label={backlog.missingCount === 0
                                        ? 'Alle Anforderungen haben Frage und Stichworte'
                                        : `${backlog.missingCount} Anforderungen ohne Frage oder Stichworte`} />
                                {backlog.pendingCount > 0 && (
                                    <Chip size="small" color="info" label={`${backlog.pendingCount} in Arbeit`} />
                                )}
                            </Stack>
                        )}

                        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                            <Button variant="outlined"
                                disabled={busy || loading || !form.enabled || backlog?.missingCount === 0}
                                startIcon={<FontAwesomeIcon icon={faWandMagicSparkles} />}
                                onClick={generateQuestions}
                            >
                                Fehlende Fragen erzeugen
                            </Button>
                            <Button size="small" disabled={busy} onClick={loadBacklog}>
                                Stand aktualisieren
                            </Button>
                        </Stack>

                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                            Alles Erzeugte ist als ungeprüft markiert und im Stammdaten-Editor bestätigbar.
                        </Typography>

                        {!form.enabled && (
                            <Alert severity="info" sx={{ mt: 2 }}>
                                Der Assistent ist abgeschaltet — erst einschalten und speichern.
                            </Alert>
                        )}
                    </Section>
                </Grid>

                {/* ---------- Spracherkennung ---------- */}
                <Grid size={12}>
                    <Section
                        icon={faMicrophone}
                        title="Spracherkennung"
                        chip={<Chip size="small" variant="outlined" label="eigener Anbieter" />}
                        hint="Bewusst getrennt vom Textmodell: zwei Dienste ohne Berührungspunkte — und Anthropic transkribiert kein Audio."
                    >
                        <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
                            Noch nicht angebunden. Die Einstellungen lassen sich vorbereiten, verwendet
                            werden sie erst mit dem Diktat.
                        </Alert>

                        <FormControlLabel
                            control={<Switch checked={form.speechEnabled}
                                onChange={(e) => set('speechEnabled', e.target.checked)} />}
                            label="Spracherkennung eingeschaltet"
                        />

                        <Grid container spacing={2} sx={{ mt: 0.5 }}>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <TextField select fullWidth size="small" label="Anbieter"
                                    value={form.speechProvider}
                                    onChange={(e) => set('speechProvider', e.target.value as SpeechProvider)}
                                >
                                    <MenuItem value="LocalWhisper">Whisper (lokal)</MenuItem>
                                    <MenuItem value="OpenAiCompatible">OpenAI-kompatibel (gehostet)</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid size={{ xs: 12, md: 5 }}>
                                <TextField fullWidth size="small" label="Basis-URL"
                                    value={form.speechBaseUrl}
                                    error={form.speechEnabled && !form.speechBaseUrl.trim()}
                                    onChange={(e) => set('speechBaseUrl', e.target.value)}
                                    placeholder="http://192.168.1.160:9000" />
                            </Grid>
                            <Grid size={{ xs: 12, md: 3 }}>
                                <TextField fullWidth size="small" label="Modell"
                                    value={form.speechModel}
                                    onChange={(e) => set('speechModel', e.target.value)} />
                            </Grid>
                            <Grid size={12}>
                                {secretField('API-Schlüssel (Sprache)', hasSpeechApiKey, speechApiKey,
                                    setSpeechApiKey, form.speechProvider === 'OpenAiCompatible' && form.speechEnabled)}
                            </Grid>
                        </Grid>
                    </Section>
                </Grid>

                {/* ---------- Testergebnis ---------- */}
                {test && (
                    <Grid size={12}>
                        <Alert severity={!test.success ? 'error' : test.withinLatencyBudget ? 'success' : 'warning'}>
                            <AlertTitle>
                                {!test.success ? 'Verbindung fehlgeschlagen'
                                    : test.withinLatencyBudget ? 'Verbindung steht'
                                        : 'Verbindung steht, aber zu langsam'}
                            </AlertTitle>
                            {test.message}
                            {test.reply && (
                                <Typography variant="caption" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                                    {`Antwort: „${test.reply}“`}
                                </Typography>
                            )}
                        </Alert>
                    </Grid>
                )}
            </Grid>

            {/*
              * Aktionsleiste klebt am unteren Rand: die Seite ist lang, und ein Speichern-Knopf, den
              * man erst hinunterscrollen muss, wird vergessen.
              */}
            <Paper
                elevation={3}
                sx={{
                    position: 'sticky', bottom: 0, mt: 2, p: 1.5, zIndex: 2,
                    borderTop: 1, borderColor: 'divider',
                }}
            >
                {busy && <LinearProgress sx={{ mb: 1 }} />}

                {problems.length > 0 && (
                    <Alert severity="warning" sx={{ mb: 1 }}>
                        Bitte zuerst korrigieren: {problems.join(' ')}
                    </Alert>
                )}

                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}>
                    <Button variant="contained" onClick={save}
                        disabled={busy || loading || !dirty || problems.length > 0}>
                        Speichern
                    </Button>
                    <Button size="small" disabled={busy || !dirty} onClick={() => void load()}>
                        Verwerfen
                    </Button>

                    {dirty && <Chip size="small" color="warning" variant="outlined" label="Ungespeicherte Änderungen" />}

                    <Box sx={{ flexGrow: 1 }} />

                    <Divider orientation="vertical" flexItem />
                    <Tooltip title={dirty ? 'Erst speichern — getestet wird der gespeicherte Stand.' : ''}>
                        <span>
                            <Button variant="outlined" size="small" disabled={busy || dirty}
                                startIcon={<FontAwesomeIcon icon={faPlugCircleCheck} />}
                                onClick={() => void runTest(false)}
                            >
                                Großes Modell testen
                            </Button>
                        </span>
                    </Tooltip>
                    <Tooltip title={dirty ? 'Erst speichern — getestet wird der gespeicherte Stand.' : ''}>
                        <span>
                            <Button variant="outlined" size="small" disabled={busy || dirty}
                                startIcon={<FontAwesomeIcon icon={faGaugeHigh} />}
                                onClick={() => void runTest(true)}
                            >
                                Schnelles Modell testen
                            </Button>
                        </span>
                    </Tooltip>
                </Stack>
            </Paper>
        </Box>
    );
};

export default AiSettings;
